import React, { useEffect, useState } from 'react';
import { 
  User, 
  DollarSign, 
  Edit3, 
  Trash2, 
  Users, 
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { InventoryScheduleDocument } from '../../types';

// Use real types from API
type Slot = InventoryScheduleDocument;

interface SlotCardProps {
  slot: Slot;
  index?: number;
  onUpdateSlot?: (slotId: string, updates: Partial<Slot>) => void;
  onAssignCoordinator?: (slot: Slot) => void;
  onViewExpenses?: (slot: Slot) => void;
  onViewSlotDetails?: (slot: Slot) => void;
  onRemoveCoordinator?: (slotId: string, coordinatorId: string, coordinatorAssociationId: string) => void;
  onConfirmAllCoordinators?: (slotId: string) => void;
}

export const getDesignation = (level: number) => {
  if (level === 3) return 'Head';
  if (level === 2) return 'Main';
  if (level === 1) return 'Trainee';
  return 'NA';
};

export const getDesignationColor = (level: number) => {
  if (level === 3) return 'bg-red-100 text-red-800 border-red-200';
  if (level === 2) return 'bg-blue-100 text-blue-800 border-blue-200';
  if (level === 1) return 'bg-green-100 text-green-800 border-green-200';
  return 'bg-gray-100 text-gray-800 border-gray-200';
};

const SlotCard: React.FC<SlotCardProps> = ({ 
  slot, 
  onUpdateSlot,
  onAssignCoordinator,
  onViewExpenses,
  onViewSlotDetails,
  onRemoveCoordinator,
  onConfirmAllCoordinators
}) => {
  const [isActive, setIsActive] = useState(true);
  const [isButtonActive, setIsButtonActive] = useState(true);
  const [isDeactivateButtonActive, setIsDeactivateButtonActive] = useState(true);
  const [selectedSlotData, setSelectedSlotData] = useState(slot);

  const {
    id, route, displayTime, maxSlots,
    partialPaymentAmount,
    price, currentBookingCount,
    tripImage, tripTitle, coordinators,
    active, inventoryId, startTime, endTime
  } = selectedSlotData;

  const coordinatorAssigned = coordinators && coordinators?.length > 0;
  const isOld = endTime < new Date().getTime();

  useEffect(() => {
    setSelectedSlotData(slot);
  }, [slot]);

  let hasTentativeCoordinator = false;

  const handleToggleStatus = async () => {
    if (!isDeactivateButtonActive) return;
    
    setIsDeactivateButtonActive(false);
    onUpdateSlot?.(id, { active: !active });
    
    setTimeout(() => {
      setIsDeactivateButtonActive(true);
    }, 2500);
  };

  const handleAssignCoordinator = () => {
    onAssignCoordinator?.(selectedSlotData);
  };

  const handleViewExpenses = () => {
    onViewExpenses?.(selectedSlotData);
  };

  const handleViewSlotDetails = () => {
    onViewSlotDetails?.(selectedSlotData);
  };

  const handleRemoveCoordinator = (coordinatorId: string, coordinatorAssociationId: string) => {
    if (!isActive) return;
    
    setIsActive(false);
    onRemoveCoordinator?.(id, coordinatorId, coordinatorAssociationId);
    
    setTimeout(() => {
      setIsActive(true);
    }, 1000);
  };

  const handleConfirmAllCoordinators = () => {
    if (!isButtonActive) return;
    
    setIsButtonActive(false);
    onConfirmAllCoordinators?.(id);
    
    setTimeout(() => {
      setIsButtonActive(true);
    }, 2000);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 max-h-[620px] min-w-[320px]">
      {/* Trip Image */}
      <div className="relative h-20 bg-gradient-to-r from-blue-500 to-purple-600">
        <img 
          src={tripImage || '/images/default-trip.jpg'} 
          alt={tripTitle}
          className="w-full h-full object-cover"
        />
        
        {/* Overlay Chips */}
        <div className="absolute top-2 left-2 flex space-x-2">
          {route && (
            <span className="bg-white/90 text-gray-700 px-2 py-1 rounded text-sm font-medium">
              {route}
            </span>
          )}
          {!active && (
            <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm font-medium">
              Cancelled
            </span>
          )}
        </div>
        
        <div className="absolute top-2 right-2">
          <span className="bg-white/90 text-gray-700 px-2 py-1 rounded text-sm font-medium">
            {displayTime}
          </span>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4">
        {/* Trip Title */}
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-gray-900 truncate">
            {tripTitle?.slice(0, 30) + (tripTitle?.length > 30 ? "..." : "")}
          </h3>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 mb-3"></div>

        {/* Price and Capacity */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-1">
            <DollarSign className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">
              ₹{price} (₹{partialPaymentAmount})
            </span>
          </div>
          <div className="text-sm text-gray-600">
            ({currentBookingCount} / {maxSlots})
          </div>
          {!isOld && (
            <button
              onClick={handleToggleStatus}
              disabled={!isDeactivateButtonActive}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                active 
                  ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              } ${!isDeactivateButtonActive ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {active ? (
                <Trash2 className="h-3 w-3" />
              ) : (
                'Activate'
              )}
            </button>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 mb-3"></div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex space-x-2">
            <button
              onClick={handleAssignCoordinator}
              className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              title="Assign Coordinator"
            >
              <User className="h-4 w-4" />
            </button>
            <button
              onClick={handleViewExpenses}
              className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="View Expenses"
            >
              <DollarSign className="h-4 w-4" />
            </button>
            <button
              onClick={handleViewSlotDetails}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="View Details"
            >
              <Edit3 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 mb-3"></div>

        {/* Coordinators */}
        <div className="space-y-2">
          {coordinatorAssigned && coordinators?.map((coordinator) => {
            if (coordinator.confirmed === false) {
              hasTentativeCoordinator = true;
            }
            
            return (
              <div key={coordinator?.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-7 h-7 rounded-full border-2 ${
                    coordinator?.confirmed ? 'border-primary-500' : 'border-red-500'
                  } bg-gray-200 flex items-center justify-center`}>
                    {coordinator?.coordinatorPic ? (
                      <img 
                        src={`https://res.cloudinary.com/travelmanage/image/upload/v1657687406/${coordinator?.coordinatorPic}`}
                        alt={coordinator?.coordinatorName}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <User className="h-4 w-4 text-gray-500" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-900 truncate max-w-[100px]">
                    {coordinator?.coordinatorName?.slice(0, 16) + (coordinator?.coordinatorName?.length > 16 ? ".." : "")}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium border ${getDesignationColor(coordinator?.level)}`}>
                    {getDesignation(coordinator?.level)}
                  </span>
                </div>
                {isActive && (
                  <button
                    onClick={() => handleRemoveCoordinator(coordinator.coordinatorId, coordinator.id)}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}
          
          {!coordinatorAssigned && (
            <div className="text-sm text-gray-500 italic">Not Assigned</div>
          )}
          
          {coordinatorAssigned && hasTentativeCoordinator && (
            <button
              onClick={handleConfirmAllCoordinators}
              disabled={!isButtonActive}
              className={`w-full mt-2 px-3 py-2 bg-primary-100 text-primary-700 rounded-lg text-sm font-medium hover:bg-primary-200 transition-colors ${
                !isButtonActive ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Confirm Coordinators
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SlotCard;
